// Customizable Area Start
import React from "react";
import { createStyles, Theme } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Button,
  IconButton,
  withStyles,
  Checkbox,
  styled,
  CircularProgress,
  Modal,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EverybrandLibraryController from "./EverybrandLibraryController.web";
import { getMediafileType } from "../../utilities/src/GetMediafileType";
import CreateEveryreelSettings from "./CreateEveryreelSettings.web";
import { PostTypes } from "./enums";

const styles = (theme: Theme) =>
  createStyles({
  });
const webStyles = {
  hidePostConfirmationModal: {
    position: "relative" as const,  // Add 'as const' to ensure type safety
    backgroundColor: "#fff",
    padding: "85px 106px 31px 72px",
    width: "100%",
    maxWidth: "636px",
    borderRadius: "0px 0px 40px 0",
    top: "50%",
    transform: "translate(-50%, -50%)",
    left: "50%",
  },
};

const LibraryModalContainer = styled(Box)(({ theme }) => ({

  position: "relative",
  display: "flex",
  height: "100%",
  flexBasis: "1171px",
  flexShrink: 0,
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "64px 44px 37px",
  gap: "22px",
  borderRadius: "8px 8px 32px 8px",
  background: "#FFF",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    minHeight: "100%",
    padding: "10px",
  },

  "& .closeButton": {
    position: "absolute",
    top: "21px",
    right: "12px",
    [theme.breakpoints.down("sm")]: {
      top: "0px"
    },
  },

"& .liveTag": {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255, 198, 41, 0.3)", // semi-transparent overlay
  color: "#000",
  fontWeight: 700,
  fontSize: "24px",
  zIndex: 4,
  pointerEvents: "none",
  borderRadius: "0px", // full width, no rounded corners
},


  "& .libraryModalTitle": {
    alignSelf: "flex-start",
    fontSize: "32px",
    fontWeight: 700,
    lineHeight: "32px",
    letterSpacing: "-0.13px",
    [theme.breakpoints.down(950)]: {
      fontSize: "24px",
    },
    [theme.breakpoints.down(650)]: {
      fontSize: "20px"
    },
  },

  "& .filesContainer": {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    width: "100%",

    "&::-webkit-scrollbar": {
      width: "10px",
    },

    "&::-webkit-scrollbar-track": {
      visibility: "hidden",
    },

    "&::-webkit-scrollbar-thumb": {
      background: "#D9D9D9",
    },
  },

"& .filePreviewContainer": {
  position: "relative",
  width: "100%",          // 🔥 important for grid
  height: "300px",
  overflow: "hidden",
  borderRadius: "8px",

  "& img, & video": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
},

  "& .filePreviewOverlay": {
    position: "absolute",
    top: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },

  "& .fileCheckbox": {
    position: "absolute",
    bottom: "13px",
    right: "13px",
    zIndex: 2,
  },

  "& .selectedFileNumber": {
    position: "absolute",
    top: "12px",
    right: "12px",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: "#FFC629",
    color: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: 700,
    zIndex: 3,
  },

  "& .buttonsContainer": {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "15px",
  },

  "& .button": {
    width: "132px",
    height: "44px",
    textTransform: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 700,
  },

  "& .loaderContainer": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "20px",
  },

  "& .errorContainer": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    padding: "20px",
    textAlign: "center",
  },

  "& .errorMessage": {
    color: "#d32f2f",
    fontSize: "18px",
    fontWeight: 500,
    marginBottom: "20px",
  },

  "& .retryButton": {
    marginTop: "10px",
    textTransform: "none",
  },

  "& .videoLoader": {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 5,
    color: "#FFC629",
  },

}))

const HidePostModalTitle = styled(Typography)({
  paddingBottom: "21px",
  fontStyle: "normal",
  justifyContent: "center",
  position: "relative",
  textAlign: "center",
  color: "#0F172A",
  letterSpacing: "-0.12px",
  marginBottom: "38px",
  fontFamily: "Inter",
  fontSize: "24px",
  fontWeight: 700,
  lineHeight: "32px" /* 133.333% */,
  display: "flex",
});

const DeleteHidePostModalFooter = styled(Box)({
  alignItems: "center",
  gap: "20px",
  justifyContent: "space-between",
  display: "flex",
});
const CancelHidePostModalButton = styled("button")({
  color: "black",
  padding: "16px 0",
  backgroundColor: "#ffca29",
  textTransform: "capitalize",
  display: "block",
  border: "none",
  fontWeight: 700,
  borderRadius: "12px",
  fontSize: "16px",
  cursor: "pointer",
  width: "100%",
});

const DeleteHidePostModalButton = styled("button")({
  border: "none",
  backgroundColor: "black",
  borderRadius: "12px",
  color: "white",
  width: "100%",
  padding: "16px 0",
  textTransform: "capitalize",
  fontSize: "16px",
  fontWeight: 700,
  cursor: "pointer",
});

export class EverybrandLibrary extends EverybrandLibraryController {

  render() {
    const { classes,goBack, libraryType } = this.props;
    const { filesFromLibrary, selectedFiles, loading, error, uploadFunnelFilesLoading } = this.state;

    if (uploadFunnelFilesLoading && !this.state.showSettings) {
      return (
        <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 300 }}>
          <CircularProgress style={{ color: "#FFC629" }} size={60} />
        </Box>
      );
    }

    return this.state.showSettings ? (
          <CreateEveryreelSettings
            postType={PostTypes.INTERACTIVE_FUNNEL}
            files={this.state.files}
            savedFunnelFiles={this.state.savedFunnelFiles || []}
            setStep={() => { }}
            closeModal={() => {
              this.closeSettings();
            }}
            addFiles={(files) => {
              this.addFiles(files);
            }}
            removeChoice={this.removeChoice}
            isUploading={this.state.uploadFunnelFilesLoading}
          />
        ) :(
      <LibraryModalContainer
        id="everybrandLibraryModal"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        { (
          <React.Fragment>
            <IconButton
              id="closeEveryreelModalButton"
              className="closeButton"
              onClick={goBack}
            >
              <CloseIcon />
            </IconButton>
            {libraryType === "SOCIAL_SCROLL" ?
              <Typography className="libraryModalTitle">
                Everybrand Library
              </Typography>
              : !this.state.showSettings ?
                <Typography className="libraryModalTitle">
                  Brandly Library
                </Typography> : null
            }
            <Box className="filesContainer">
              {loading ? (
                /* --- LOADING STATE --- */
                <Box className="loaderContainer">
                  <CircularProgress style={{ color: "#FFC629" }} size={60} />
                  <Typography style={{ color: "#666" }}>
                    Loading library files...
                  </Typography>
                </Box>
              ) : error ? (
                /* --- ERROR STATE --- */
                <Box className="errorContainer">
                  <Typography className="errorMessage">{error}</Typography>
                  <Button
                    className="retryButton"
                    variant="contained"
                    style={{ backgroundColor: "#FFC629", color: "#000" }}
                    onClick={() => {
                      if (libraryType === "SOCIAL_SCROLL") {
                        this.getMediaFiles();
                      } else {
                        this.getBeanlyLibrary();
                      }
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              ) : libraryType === "SOCIAL_SCROLL" ? (
                /* --- SOCIAL_SCROLL VIEW --- */
                <>
                  {filesFromLibrary.length === 0 ? (
                    <Box className="errorContainer">
                      <Typography style={{ color: "#666" }}>
                        No files found in library.
                      </Typography>
                    </Box>
                  ) : (
                    filesFromLibrary.map((file, i) => (
                      <Box
                        id={`file-${i}`}
                        key={file.id}
                        className="filePreviewContainer"
                        onClick={() => this.selectFile(i)}
                      >
                        {getMediafileType(file.attributes.files) === "image" ? (
                          <img
                            src={file.attributes.files}
                            alt="file from library"
                            className="filePreview"
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <>
                            <video
                              controls={false}
                              onLoadStart={() => this.setState(prev => ({ videoLoadingMap: { ...prev.videoLoadingMap, [file.id]: true } }))}
                              onCanPlay={() => this.setState(prev => ({ videoLoadingMap: { ...prev.videoLoadingMap, [file.id]: false } }))}
                            >
                              <source src={file.attributes.files} type="video/mp4" />
                            </video>
                            {this.state.videoLoadingMap[file.id] && <CircularProgress className="videoLoader" />}
                          </>
                        )}

                        <Box
                          className="filePreviewOverlay"
                          style={{
                            backgroundColor: "#000",
                            opacity: selectedFiles.some(
                              sf => sf.id === file.id
                            ) ? 0.5 : 0
                          }}
                        >
                          <Checkbox
                            className="fileCheckbox"
                            checked={selectedFiles.some(sf => sf.id === file.id)}
                            disabled={selectedFiles.length >= 10}
                          />

                          {selectedFiles.some(sf => sf.id === file.id) && (
                            <Typography className="selectedFileNumber">
                              {selectedFiles.findIndex(sf => sf.id === file.id) + 1}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                </>
              ) : (
                /* --- FUNNEL OR UNDEFINED VIEW --- */
                <>
                  {!Array.isArray(this.state.brndlyReels?.data) ||
                    this.state.brndlyReels.data.length === 0 ? (
                    <Box className="errorContainer">
                      <Typography style={{ color: "#666" }}>
                        No files found in Brandly library.
                      </Typography>
                    </Box>
                  ) : (
                    this.state.brndlyReels.data.map((file: any, i: any) => {

                      const mediaUrl =
                        file?.attributes?.video ||
                        file?.attributes?.social_videos?.[0]?.link ||
                        "";

                      const mediaType = getMediafileType(mediaUrl);
                      const isSelected = selectedFiles.some(sf => sf.id === file.id);

                      return (
                        <Box
                          id={`file-${i}`}
                          key={file.id}
                          className="filePreviewContainer"
                          onClick={() => this.selectBrndlyFile(i)}
                          style={isSelected ? { border: '5px solid #FFC629', position: "relative" } : { position: "relative" }}
                        >
                          {mediaType === "image" ? (
                            <img src={mediaUrl} alt="file" className="filePreview" />
                          ) : file?.attributes?.thumbnail?.url ? (
                            <img src={file.attributes.thumbnail.url} alt="thumbnail" className="filePreview" style={{ objectFit: 'scale-down'}} />
                          ) : (
                            <>
                              <video
                                controls={false}
                                onLoadStart={() => this.setState(prev => ({ videoLoadingMap: { ...prev.videoLoadingMap, [file.id]: true } }))}
                                onCanPlay={() => this.setState(prev => ({ videoLoadingMap: { ...prev.videoLoadingMap, [file.id]: false } }))}
                              >
                                <source src={mediaUrl} type="video/mp4" />
                              </video>
                              {this.state.videoLoadingMap[file.id] && <CircularProgress className="videoLoader" />}
                            </>
                          )}

                          <Box
                            className="filePreviewOverlay"
                            style={{
                              backgroundColor: "#000",
                              opacity: isSelected ? 0.5 : 0
                            }}
                          >
                          </Box>
                          {file?.attributes?.is_live && (<React.Fragment>
                            {/* LIVE TAG - Centered */}
                            <Box className="liveTag">LIVE</Box>
                          </React.Fragment>)}

                        </Box>
                      );
                    })
                  )}
                </>
              )}
            </Box>
          </React.Fragment>
        )}

        {(!this.state.showSettings && selectedFiles?.length > 0) && (<Box className="buttonsContainer">
          <Button
            id="discardLibraryButton"
            className="button"
            style={{
              backgroundColor: "#000",
              border: "1px solid #FFC629",
              color: "#FFF",
            }}
            onClick={libraryType === "SOCIAL_SCROLL" ? goBack : this.openDeleteBrandlyConfirmationModal}
          >
            Discard
          </Button>
          <Button
            id="confirmLibraryFilesButton"
            className="button"
            style={{ backgroundColor: "#FFC629", color: "#000" }}
            onClick={this.acceptFiles}
            disabled={!selectedFiles.length || (libraryType !== "SOCIAL_SCROLL" && (selectedFiles.length > 1 || selectedFiles.some(f => f.attributes?.is_live)))} //  || this.state.brndlyReels?.data?.some((f:any) => f.attributes?.social_videos?.[0]?.is_live)
          >
            Accept
          </Button>
        </Box>)}
        <Modal
          open={this.state.deleteBrandlyConfirmationModal}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          data-test-id="deletePostModal"
        >
          <Box style={webStyles.hidePostConfirmationModal}>
            <div>
              <HidePostModalTitle>
                Delete {this.state.selectedFiles.length > 1 ? "Selected Brandly Flows" : "This Brandly"}
                <IconButton
                  id="closeDeleteBrandlyConfirmationModal"
                  className="closeButton"
                  onClick={this.openDeleteBrandlyConfirmationModal}
                  style={{
                    position: "absolute",
                    cursor: "pointer",
                    right: "-71px",
                    height: "26px",
                    bottom: "81px",
                    padding: "4px",
                    width: "24px",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <CloseIcon />
                </IconButton>

              </HidePostModalTitle>
            </div>
            <DeleteHidePostModalFooter>
              <DeleteHidePostModalButton
                data-test-id="cancelDeletePost"
                type="button"
                onClick={this.openDeleteBrandlyConfirmationModal}
              >
                Back
              </DeleteHidePostModalButton>
              <CancelHidePostModalButton
                data-test-id="confirmDeletePost"
                onClick={(event) => {
                  this.postDeleteFunnel();
                }}
              >
                Confirm
              </CancelHidePostModalButton>
            </DeleteHidePostModalFooter>
          </Box>
        </Modal>
      </LibraryModalContainer>
    );
  }
}

export default withStyles(styles)(EverybrandLibrary);

// Customizable Area End
